import { useState, useEffect } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { Phone, Menu, X, ChevronDown, Mail, MapPin, Lock, ExternalLink } from "lucide-react";
import CookieBanner, { openCookieBanner } from "./CookieBanner";
import { NAV, PHONE, PHONE_HREF, EMAIL, ADDRESS, OWNER_NOTICE, SISTER, FRANCHISE_DEPTS } from "@/data/site";

const Logo = () => (
  <Link to="/" className="flex items-center gap-2.5" data-testid="header-logo-link" aria-label="TERMITE HABITAT – accueil">
    <img src="/images/logo.webp" alt="Logo TERMITE HABITAT" width="44" height="44" className="h-11 w-11" />
    <span className="font-display text-lg font-black leading-none tracking-tight">
      TERMITE<span className="block text-[11px] font-semibold tracking-[0.3em] text-[#C06014]">HABITAT</span>
    </span>
  </Link>
);

function DesktopNav() {
  return (
    <nav className="hidden items-center gap-1 lg:flex" aria-label="Navigation principale">
      {NAV.map((item) =>
        item.children ? (
          <div key={item.label} className="group relative">
            <button className="flex items-center gap-1 rounded-full px-3 py-2 text-sm font-semibold hover:bg-black/5" data-testid={`nav-${item.label.toLowerCase()}`}>
              {item.label} <ChevronDown className="h-3.5 w-3.5 transition-transform group-hover:rotate-180" />
            </button>
            <div className="invisible absolute left-0 top-full w-64 translate-y-2 rounded-xl border bg-white p-2 opacity-0 shadow-xl transition-[opacity,transform] group-hover:visible group-hover:translate-y-0 group-hover:opacity-100">
              {item.children.map((c) => (
                <NavLink key={c.to} to={c.to} className="block rounded-lg px-3 py-2 text-sm hover:bg-[#F5F3E9] hover:text-[#C06014]" data-testid={`nav-link-${c.to.slice(1)}`}>
                  {c.label}
                </NavLink>
              ))}
            </div>
          </div>
        ) : (
          <NavLink key={item.to} to={item.to} data-testid={`nav-link-${item.to.slice(1)}`}
            className={({ isActive }) => `rounded-full px-3 py-2 text-sm font-semibold hover:bg-black/5 ${isActive ? "text-[#C06014]" : ""}`}>
            {item.label}
          </NavLink>
        )
      )}
    </nav>
  );
}

function MobileNav({ open, onClose }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 top-[72px] z-[60] overflow-y-auto bg-[#F5F3E9] p-6 pb-28 lg:hidden" data-testid="mobile-menu">
      {NAV.map((item) => (
        <div key={item.label} className="border-b py-3">
          {item.children ? (
            <>
              <p className="mb-2 text-xs font-bold uppercase tracking-widest text-[#53634F]">{item.label}</p>
              {item.children.map((c) => (
                <Link key={c.to} to={c.to} onClick={onClose} className="block py-1.5 text-lg font-semibold">{c.label}</Link>
              ))}
            </>
          ) : (
            <Link to={item.to} onClick={onClose} className="block text-lg font-semibold">{item.label}</Link>
          )}
        </div>
      ))}
      <Link to="/contact" onClick={onClose} className="mt-6 block rounded-full bg-[#C06014] py-3 text-center font-bold text-white">Demander un devis</Link>
    </div>
  );
}

function Header() {
  const [open, setOpen] = useState(false);
  const loc = useLocation();
  useEffect(() => { setOpen(false); window.scrollTo(0, 0); }, [loc.pathname]);
  return (
    <>
    <header className="sticky top-0 z-50 border-b border-black/5 bg-[#F5F3E9]/85 backdrop-blur-xl">
      <div className="container-x flex h-[72px] items-center justify-between gap-4">
        <Logo />
        <DesktopNav />
        <div className="flex items-center gap-2">
          <a href={PHONE_HREF} className="btn-lift hidden items-center gap-2 rounded-full bg-[#1E2422] px-4 py-2.5 text-sm font-bold text-white sm:flex" data-testid="header-phone-button">
            <Phone className="h-4 w-4" /> {PHONE}
          </a>
          <Link to="/contact" className="btn-lift hidden rounded-full bg-[#C06014] px-4 py-2.5 text-sm font-bold text-white md:block" data-testid="header-quote-button">Devis 48 h</Link>
          <button className="rounded-full p-2 lg:hidden" onClick={() => setOpen(!open)} aria-label="Menu" data-testid="mobile-menu-toggle">
            {open ? <X /> : <Menu />}
          </button>
        </div>
      </div>
    </header>
    <MobileNav open={open} onClose={() => setOpen(false)} />
    </>
  );
}

const FooterCol = ({ title, links }) => (
  <div>
    <p className="mb-4 font-display text-sm font-bold uppercase tracking-widest text-[#E9A46A]">{title}</p>
    <ul className="space-y-2 text-sm text-white/75">
      {links.map(([to, label]) => (
        <li key={to}><Link to={to} className="hover:text-white" data-testid={`footer-link-${to.slice(1) || "home"}`}>{label}</Link></li>
      ))}
    </ul>
  </div>
);

function Footer() {
  return (
    <footer className="relative bg-[#1E2422] pb-24 pt-16 text-white md:pb-10">
      <div className="container-x grid gap-10 md:grid-cols-2 lg:grid-cols-5">
        <div className="lg:col-span-2">
          <div className="flex items-center gap-3">
            <img src="/images/logo.webp" alt="Logo TERMITE HABITAT" className="h-14 w-14" loading="lazy" />
            <p className="font-display text-2xl font-black">TERMITE HABITAT<sup className="text-xs">®</sup></p>
          </div>
          <p className="mt-4 max-w-sm text-sm text-white/70">Traitement des termites, capricornes, vrillettes, moustiques et humidité. Garantie commerciale 5 ans (hors pose de pièges).</p>
          <ul className="mt-5 space-y-2 text-sm">
            <li><a href={PHONE_HREF} className="flex items-center gap-2 hover:text-[#E9A46A]"><Phone className="h-4 w-4" /> {PHONE}</a></li>
            <li><a href={`mailto:${EMAIL}`} className="flex items-center gap-2 hover:text-[#E9A46A]"><Mail className="h-4 w-4" /> {EMAIL}</a></li>
            <li className="flex items-center gap-2 text-white/70"><MapPin className="h-4 w-4 shrink-0" /> {ADDRESS}</li>
          </ul>
        </div>
        <FooterCol title="Traitements" links={[["/traitement-termites", "Termites"], ["/traitement-capricorne", "Capricorne"], ["/traitement-vrillette", "Vrillette"], ["/traitement-moustiques", "Moustiques"], ["/traitement-humidite", "Humidité"], ["/traitements-preventif-curatif", "Préventif & curatif"]]} />
        <FooterCol title="Entreprise" links={[["/services", "Services"], ["/methodes", "Méthodes"], ["/solutions", "Solutions"], ["/secteurs", "Secteurs"], ["/certibiocide", "Certibiocide"], ["/garantie-5-ans", "Garantie 5 ans"], ["/a-propos", "À propos"], ["/blog", "Blog & conseils"], ["/contact", "Contact"]]} />
        <div>
          <FooterCol title="Réseau" links={[["/franchise", "Devenir franchisé"], ...FRANCHISE_DEPTS.slice(0, 3).map((d) => [`/franchise#dep-${d.code}`, `Franchise ${d.name} (${d.code})`])]} />
          <p className="mb-3 mt-6 font-display text-sm font-bold uppercase tracking-widest text-[#E9A46A]">Nos sites</p>
          <ul className="space-y-2 text-sm text-white/75">
            {Object.values(SISTER).map((s) => (
              <li key={s.url}><a href={s.url} target="_blank" rel="noopener" className="flex items-center gap-1.5 hover:text-white" data-testid={`footer-sister-${s.label}`}>{s.label} <ExternalLink className="h-3 w-3" /></a></li>
            ))}
          </ul>
          <Link to="/espace" className="mt-6 inline-flex items-center gap-2 rounded-full border border-white/20 px-4 py-2 text-sm font-semibold hover:bg-white hover:text-[#1E2422]" data-testid="footer-franchise-space-link">
            <Lock className="h-4 w-4" /> Espace franchisé
          </Link>
        </div>
      </div>
      <div className="container-x mt-12 border-t border-white/10 pt-6 text-xs text-white/55">
        <p className="mb-2">{OWNER_NOTICE} Marque déposée à l'INPI.</p>
        <div className="flex flex-wrap gap-x-5 gap-y-2">
          <span>© {new Date().getFullYear()} TERMITE HABITAT – EI BARBIER Marc</span>
          <Link to="/mentions-legales" className="hover:text-white" data-testid="footer-legal-link">Mentions légales</Link>
          <Link to="/politique-confidentialite" className="hover:text-white" data-testid="footer-privacy-link">Confidentialité & cookies</Link>
          <Link to="/cgv" className="hover:text-white" data-testid="footer-cgv-link">CGV</Link>
          <button onClick={openCookieBanner} className="hover:text-white" data-testid="footer-cookies-button">Gérer les cookies</button>
        </div>
      </div>
    </footer>
  );
}

const MobileCallBar = () => (
  <div className="fixed inset-x-0 bottom-0 z-40 grid grid-cols-2 gap-2 border-t bg-white/95 p-2 backdrop-blur md:hidden no-print">
    <a href={PHONE_HREF} className="flex items-center justify-center gap-2 rounded-full bg-[#1E2422] py-3 text-sm font-bold text-white" data-testid="mobile-call-button"><Phone className="h-4 w-4" /> Appeler</a>
    <Link to="/contact" className="flex items-center justify-center rounded-full bg-[#C06014] py-3 text-sm font-bold text-white" data-testid="mobile-quote-button">Devis 48 h</Link>
  </div>
);

export default function PublicLayout() {
  return (
    <div className="min-h-screen">
      <a href="#main" className="sr-only focus:not-sr-only">Aller au contenu</a>
      <Header />
      <main id="main"><Outlet /></main>
      <Footer />
      <MobileCallBar />
      <CookieBanner />
    </div>
  );
}
